import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SnackbarViewport, SnackbarBox, SnackbarContent, SnackbarCloseWrap } from './styles';
import ExitButton from '@/components/atoms/UI/ExitButton/ExitButton';
import ShadowMount from '@/styles/ShadowMount';
import { InsideShadowReset } from '@/styles/IslandScope';

type PropsType = {
    children: React.ReactNode;
    onClose?: () => void;
    /** Авто-скрытие через `duration` мс (пауза на hover/focus). */
    autoHide?: boolean;
    duration?: number;
    /** По умолчанию крестик только у persistent-варианта (`!autoHide`). */
    showClose?: boolean;
};

// Отдельный от #modal-root узел: у модалки на её узле уже свой ShadowMount/портал.
const ensureSnackbarRoot = (): HTMLElement | null => {
    if (typeof document === 'undefined') return null;
    const existing = document.getElementById('snackbar-root');
    if (existing) return existing;
    const created = document.createElement('div');
    created.id = 'snackbar-root';
    document.body.appendChild(created);
    return created;
};

/**
 * Snackbar — не-модальная плашка снизу. В отличие от Modal НЕ перекрывает
 * страницу: нет оверлея, блокировки скролла, focus-trap и перехвата Esc.
 */
const Snackbar: React.FC<PropsType> = ({
    children,
    onClose,
    autoHide,
    duration = 5000,
    showClose,
}) => {
    const snackbarRoot = useMemo(ensureSnackbarRoot, []);
    const { t } = useTranslation();

    // Пауза таймера под курсором/фокусом — иначе тост с CTA пропадёт до клика.
    const [paused, setPaused] = useState(false);

    useEffect(() => {
        if (!autoHide || paused || !onClose) return;
        const id = window.setTimeout(onClose, duration);
        return () => window.clearTimeout(id);
    }, [autoHide, paused, duration, onClose]);

    if (!snackbarRoot) return null;

    const withClose = showClose ?? !autoHide;

    return (
        <ShadowMount host={snackbarRoot}>
            <InsideShadowReset />
            <SnackbarViewport role="status" aria-live="polite" aria-atomic="true">
                <SnackbarBox
                    onMouseEnter={() => setPaused(true)}
                    onMouseLeave={() => setPaused(false)}
                    onFocus={() => setPaused(true)}
                    onBlur={() => setPaused(false)}
                >
                    <SnackbarContent>
                        {children}
                    </SnackbarContent>

                    {withClose && (
                        <SnackbarCloseWrap>
                            <ExitButton
                                onClick={onClose}
                                xSmall
                                aria-label={t('snackbar.close')}
                            />
                        </SnackbarCloseWrap>
                    )}
                </SnackbarBox>
            </SnackbarViewport>
        </ShadowMount>
    );
};

export default Snackbar;
